import { FC, useEffect, useState } from "react";
import { ApiResponse } from "apisauce";
import { t } from "i18next";
import moment from "moment";
import { GoShareAndroid } from "react-icons/go";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import { Button, Modal, ModalBody, ModalFooter } from "reactstrap";
import { useAppMode } from "src/contexts/app-mode";

import Loader from "@components/shared/Loader/Loader";
import WarningMessageBox from "@components/Unit/WarningMessageBox/WarningMessageBox";
import AddToCalendar from "@components/User/Reservations/AddToCalendar/AddToCalendar";
import SharePopover from "@components/User/Reservations/SharePopover/SharePopover";
import { SHOW_BOOKING_TYPES } from "@constants/Bookings";
import { DateFormats } from "@enums/shared.enum";
import { useBookingShare } from "@hooks/useBookingShare";
import { useUserProfile } from "@hooks/useUserProfile/useUserProfile";
import { BookingParticipantModalProps } from "@models/components/booking.model";
import { CancelBookingFeasibilityData } from "@models/components/user.model";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import kioskSlice from "@slices/kiosk.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";

const BookingParticipantModal: FC<BookingParticipantModalProps> = ({
  isLoading,
  participant,
  participantModal,
  refreshCalendar,
  refreshResources,
  setParticipant,
  subType,
  toggleParticipantModal,
}) => {
  const dispatch = useDispatch();
  const { isKioskMode } = useAppMode();
  const isAuthenticated = useSelector(
    (state: RootState) => state.local.isAuthenticated
  );
  const accessToken = useSelector(
    (state: RootState) => state.local.accessToken
  );
  const { data: userProfile } = useUserProfile(accessToken, {
    enabled: isAuthenticated && !!accessToken,
  });

  const isCurrentUserOrganizer = participant?.organizer?.some(
    org => String(org.id) === String(userProfile?.profile?.id)
  );

  const [cancelFeasibility, setCancelFeasibility] =
    useState<CancelBookingFeasibilityData | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const {
    calendarEventData,
    copied,
    handleCopy,
    handleEmail,
    handleShare,
    handleWhatsApp,
    setShowSharePopover,
    showSharePopover,
  } = useBookingShare(participant);

  useEffect(() => {
    if (
      isAuthenticated &&
      isCurrentUserOrganizer &&
      participantModal &&
      participant?.bookingId
    ) {
      setIsChecking(true);
      API_SERVICES.BOOKING.CHECK_CANCEL_FEASIBILITY(participant.bookingId)
        .then((response: ApiResponse<CancelBookingFeasibilityData>) => {
          const responseData = handleResponse(response);
          if (responseData?.ok) {
            setCancelFeasibility(responseData?.data);
          }
        })
        .finally(() => {
          setIsChecking(false);
        });
    } else {
      setCancelFeasibility(null);
    }
  }, [
    isAuthenticated,
    isCurrentUserOrganizer,
    participantModal,
    participant?.bookingId,
  ]);

  const handleCancel = async () => {
    if (!participant?.bookingId) return;
    setIsCancelling(true);
    const response = await API_SERVICES.BOOKING.CANCEL_BOOKING(
      participant.bookingId
    );
    const responseData = handleResponse(response);
    setIsCancelling(false);
    if (responseData?.ok) {
      if (responseData?.data?.hasRefund === true) {
        commonUtils?.toastService(
          t(
            "bookingCancelledWithRefund",
            "Your booking has been successfully cancelled. The refund has been initiated and will be credited to your original payment source within a few working days."
          )
        );
      } else {
        commonUtils?.toastService(t("bookingCancelled"));
      }
      setCancelModalOpen(false);
      toggleParticipantModal();
      refreshCalendar?.();
      refreshResources?.();
    }
  };

  return (
    <>
      <Modal
        isOpen={participantModal}
        onClosed={() => {
          setParticipant(null);
          setCancelModalOpen(false);
        }}
        toggle={toggleParticipantModal}
        className="fg-dev-participants-modal"
        centered={true}
      >
        <div className="modal-header d-flex align-items-center justify-content-between">
          <div>
            <h5 className="mb-0">{t("Calendar.participants")}</h5>
            <div className="d-flex text-muted small mt-1">
              {participant?.startTime &&
                moment(participant?.startTime).format(DateFormats.Time)}{" "}
              {participant?.endTime && " - "}
              {participant?.endTime &&
                moment(participant?.endTime).format(DateFormats.Time)}{" "}
              {participant?.resource?.title && " . "}
              {participant?.resource?.title &&
                applyLocalization(participant?.resource?.title)}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            {!isKioskMode && isCurrentUserOrganizer && (
              <>
                <button
                  className="btn p-0 d-inline-flex align-items-center justify-content-center fg-share-btn"
                  onClick={handleShare}
                  type="button"
                  aria-label={t("shareBooking")}
                  data-tooltip-id={
                    !commonUtils.isMobile()
                      ? "fg-share-participant-booking-tooltip"
                      : undefined
                  }
                  data-tooltip-position-strategy="fixed"
                >
                  <GoShareAndroid />
                </button>
                {!commonUtils.isMobile() && (
                  <Tooltip
                    id="fg-share-participant-booking-tooltip"
                    place="left"
                    closeEvents={{ blur: true, click: true, mouseleave: true }}
                    className="fg-tooltip-container fg-z-index-1 fg-tooltip-z-index"
                  >
                    {t("shareBooking")}
                  </Tooltip>
                )}
                {calendarEventData && (
                  <AddToCalendar
                    data={calendarEventData}
                    showTextLabel={false}
                  />
                )}
              </>
            )}
            <button
              type="button"
              className="btn-close m-0"
              onClick={toggleParticipantModal}
              aria-label="Close"
            ></button>
          </div>
        </div>

        <ModalBody>
          <Loader isLoading={isLoading || isChecking || isCancelling} />

          {participant?.organizer && participant?.organizer?.length > 0 && (
            <>
              <h5>
                {subType && SHOW_BOOKING_TYPES.includes(subType)
                  ? t("Calendar.responsiblePerson")
                  : t("Calendar.organizer")}
              </h5>
              <div>
                {participant?.organizer.map(organizer => (
                  <div
                    key={organizer.id}
                    className="d-flex align-items-center fg-participants-profile"
                  >
                    <div className="d-flex justify-content-center align-items-center rounded-circle text-white text-center fg-profile-pic">
                      {organizer.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="ms-2">{organizer.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {participant?.participants &&
            participant?.participants?.length > 0 && (
              <>
                <h5 className="mt-4">{t("Calendar.participants")}</h5>
                <div>
                  {participant?.participants.map(participant => (
                    <div
                      key={participant.id}
                      className="d-flex align-items-center fg-participants-profile"
                    >
                      <div className="d-flex justify-content-center align-items-center rounded-circle text-white text-center fg-profile-pic">
                        {participant.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="ms-2">{participant.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

          {!cancelFeasibility?.cancellable && cancelFeasibility?.message && (
            <div className="mt-4">
              <WarningMessageBox message={cancelFeasibility?.message} />
            </div>
          )}
        </ModalBody>

        <ModalFooter className="justify-content-center">
          {isAuthenticated ? (
            isCurrentUserOrganizer ? (
              <Button
                color="danger"
                className="px-4"
                disabled={!cancelFeasibility?.cancellable || isChecking}
                onClick={() => setCancelModalOpen(true)}
              >
                {t("cancelBooking")}
              </Button>
            ) : (
              <Button color="secondary" onClick={toggleParticipantModal}>
                {t("Calendar.close")}
              </Button>
            )
          ) : (
            <Button
              className="btn fg-btn fg-btn-filled-darkblue mb-0 w-100"
              onClick={() => {
                toggleParticipantModal();
                dispatch(kioskSlice.actions.setShowLoginModal(true));
              }}
            >
              {t("loginToBookOrCancel", "Login to Book or Cancel")}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={cancelModalOpen}
        toggle={() => setCancelModalOpen(false)}
        centered={true}
        className="fg-dev-participants-modal"
      >
        <div className="modal-header">
          <h5>{t("cancelBooking")}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setCancelModalOpen(false)}
            aria-label="Close"
          ></button>
        </div>
        <ModalBody className="text-center py-4">
          <p className="fs-5">
            {t(
              "cancelBookingConfirmationMsg",
              "Do you really want to cancel the booking?"
            )}
          </p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <div className="d-flex justify-content-center gap-3 w-100">
            <Button
              className="btn fg-btn fg-btn-grey"
              onClick={() => setCancelModalOpen(false)}
            >
              {t("no")}
            </Button>
            <Button
              className="btn fg-btn fg-btn-lightblue"
              disabled={isCancelling}
              onClick={handleCancel}
            >
              {t("yes")}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <SharePopover
        isOpen={showSharePopover}
        onClose={() => setShowSharePopover(false)}
        copied={copied}
        handleCopy={handleCopy}
        handleEmail={handleEmail}
        handleWhatsApp={handleWhatsApp}
      />
    </>
  );
};

export default BookingParticipantModal;
